import React, { useState, useEffect, useRef } from 'react';
import { useCurrency } from '../contexts/CurrencyContext';

const GlobalCurrencySelector = ({ className = "" }) => {
  const {
    selectedCurrency,
    availableCurrencies,
    changeCurrency,
    isDetecting,
    detectionResult,
    currencySymbol
  } = useCurrency();

  const [isOpen, setIsOpen] = useState(false);
  const [isChanging, setIsChanging] = useState(false);
  const buttonRef = useRef(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });

  const handleCurrencyChange = async (newCurrency) => {
    if (newCurrency === selectedCurrency) return;
    
    try {
      setIsChanging(true);
      await changeCurrency(newCurrency);
      setIsOpen(false);
    } catch (error) {
      console.error('Failed to change currency:', error);
    } finally {
      setIsChanging(false);
    }
  };

  // Update dropdown position when opened (fixed positioning uses viewport coords)
  const updateDropdownPosition = () => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const dropdownWidth = 256; // w-64 = 16rem = 256px
      const rightOverflow = rect.left + dropdownWidth - window.innerWidth;
      setDropdownPosition({
        top: rect.bottom + 8,
        left: rightOverflow > 0 ? rect.right - dropdownWidth : rect.left
      });
    }
  };

  useEffect(() => {
    if (isOpen && buttonRef.current) {
      updateDropdownPosition();
      window.addEventListener('scroll', updateDropdownPosition, { passive: true });
      window.addEventListener('resize', updateDropdownPosition, { passive: true });
      return () => {
        window.removeEventListener('scroll', updateDropdownPosition);
        window.removeEventListener('resize', updateDropdownPosition);
      };
    }
  }, [isOpen]);

  return (
    <div className={`relative ${className}`}>
      {/* Currency Selector Button */}
      <button
        ref={buttonRef}
        onClick={() => setIsOpen(!isOpen)}
        disabled={isDetecting}
        className="flex items-center space-x-2 px-3 py-2 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
      >
        {isDetecting ? (
          <>
            <i className="fas fa-spinner fa-spin text-gray-400"></i>
            <span className="text-sm text-gray-500">Detecting...</span>
          </>
        ) : (
          <>
            <span className="text-sm font-medium">{currencySymbol}</span>
            <span className="text-sm text-gray-600">{selectedCurrency}</span>
            <i className={`fas fa-chevron-${isOpen ? 'up' : 'down'} text-xs text-gray-400`}></i>
          </>
        )}
      </button>

      {/* Detection Status */}
      {detectionResult && !isDetecting && (
        <div className="absolute top-full left-0 mt-1 text-xs text-gray-500 whitespace-nowrap">
          {detectionResult.country && (
            <span className="flex items-center">
              <i className="fas fa-map-marker-alt mr-1"></i>
              {detectionResult.country}
            </span>
          )}
        </div>
      )}

      {/* Dropdown Menu - Using fixed positioning to escape overflow constraints */}
      {isOpen && (
        <div 
          style={{
            position: 'fixed',
            top: `${dropdownPosition.top}px`,
            left: `${dropdownPosition.left}px`,
            zIndex: 60
          }}
          className="w-64 bg-white border border-gray-200 rounded-lg shadow-lg max-h-80 overflow-y-auto"
        >
          <div className="p-3 border-b border-gray-100">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-medium text-gray-900">Select Currency</h3>
              <button
                onClick={() => setIsOpen(false)}
                className="text-gray-400 hover:text-gray-600 p-2.5"
                aria-label="Close currency selector"
              >
                <i className="fas fa-times"></i>
              </button>
            </div>
          </div>

          <div className="py-2">
            {/* Popular Currencies */}
            <div className="px-3 py-2">
              <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
                Popular Currencies
              </h4>
              <div className="space-y-1">
                {['USD', 'EUR', 'GBP', 'NGN', 'KES', 'GHS', 'ZAR', 'EGP'].map(code => {
                  const currency = availableCurrencies.find(c => c.code === code);
                  if (!currency) return null;
                  
                  return (
                    <button
                      key={code}
                      onClick={() => handleCurrencyChange(code)}
                      disabled={isChanging}
                      className={`w-full flex items-center justify-between px-2 py-2 rounded text-sm hover:bg-gray-50 disabled:opacity-50 ${
                        selectedCurrency === code ? 'bg-blue-50 text-blue-700' : 'text-gray-700'
                      }`}
                    >
                      <div className="flex items-center space-x-2">
                        <span className="font-medium">{currency.symbol}</span>
                        <span>{currency.code}</span>
                        <span className="text-gray-500">{currency.name}</span>
                      </div>
                      {selectedCurrency === code && (
                        <i className="fas fa-check text-blue-600"></i>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* All Currencies */}
            <div className="px-3 py-2 border-t border-gray-100">
              <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
                All Currencies
              </h4>
              <div className="space-y-1 max-h-48 overflow-y-auto">
                {availableCurrencies.map(currency => (
                  <button
                    key={currency.code}
                    onClick={() => handleCurrencyChange(currency.code)}
                    disabled={isChanging}
                    className={`w-full flex items-center justify-between px-2 py-2 rounded text-sm hover:bg-gray-50 disabled:opacity-50 ${
                      selectedCurrency === currency.code ? 'bg-blue-50 text-blue-700' : 'text-gray-700'
                    }`}
                  >
                    <div className="flex items-center space-x-2">
                      <span className="font-medium">{currency.symbol}</span>
                      <span>{currency.code}</span>
                      <span className="text-gray-500">{currency.name}</span>
                    </div>
                    {selectedCurrency === currency.code && (
                      <i className="fas fa-check text-blue-600"></i>
                    )}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Backdrop */}
      {isOpen && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setIsOpen(false)}
        />
      )}
    </div>
  );
};

export default GlobalCurrencySelector;
